import { useEffect, useRef, useState } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import { Printer, ArrowLeft, Check, Loader2, Plus } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PLANTILLAS, FORMATOS, GRUPOS } from "@/lib/carteles";
import CartelEditor, { CARTEL_NUEVO } from "@/components/carteles/CartelEditor";
import { Cartel, PX_MM } from "@/components/carteles/CartelPlantilla";

export default function Carteles() {
  const { id } = useParams();
  // abierto desde una zona de estacionamiento: plantilla, papel, señal y datos de la zona en la dirección
  const [q] = useSearchParams();
  const desdeZona = useRef(q.get("tpl") ? Object.fromEntries(["hora", "hora_hasta", "fecha", "fecha_hasta", "motivo", "restriccion", "dias", "texto_libre"].filter((k) => q.get(k)).map((k) => [k, q.get(k)])) : null);
  const { canEdit } = useAuth();
  const [ev, setEv] = useState(null);
  const [tplId, setTplId] = useState(PLANTILLAS.some((t) => t.id === q.get("tpl")) ? q.get("tpl") : PLANTILLAS[0].id);
  const [datos, setDatos] = useState({});
  const [formato, setFormato] = useState("A4");
  const [estado, setEstado] = useState("");
  const [propios, setPropios] = useState([]); // carteles personalizados del usuario (no dependen del evento)
  const timer = useRef(null);
  const marco = useRef(null);
  const [ancho, setAncho] = useState(0);
  useEffect(() => {
    if (!marco.current) return undefined;
    const ro = new ResizeObserver(([e]) => setAncho(e.contentRect.width));
    ro.observe(marco.current);
    return () => ro.disconnect();
  }, [ev, tplId.startsWith("p:")]); // eslint-disable-line react-hooks/exhaustive-deps
  const tpl = PLANTILLAS.find((t) => t.id === tplId);
  const propio = tplId.startsWith("p:") ? propios.find((p) => `p:${p.id}` === tplId) : null;

  useEffect(() => { api.get(`/events/${id}`).then((r) => setEv(r.data)).catch((e) => toast.error(errMsg(e))); }, [id]);
  useEffect(() => { api.get("/carteles-personalizados").then((r) => setPropios(r.data)).catch((e) => toast.error(errMsg(e))); }, []);
  useEffect(() => {
    if (!ev || !tpl) return;
    const guardado = ev.carteles?.[tplId] || {};
    const zona = desdeZona.current && tplId === q.get("tpl") ? { ...desdeZona.current, ...(q.get("senal") === "0" ? { senal: false } : { senal: true }) } : {};
    setDatos({ ...tpl.defaults(ev), ...guardado, ...zona });
    const fq = desdeZona.current && tplId === q.get("tpl") && FORMATOS[q.get("formato")] ? q.get("formato") : null;
    setFormato(fq || guardado.formato || "A4");
  }, [ev, tplId]); // eslint-disable-line react-hooks/exhaustive-deps

  // guardado automático de lo que se teclea (como en el editor)
  const cambiar = (patch) => {
    const n = { ...datos, ...patch };
    setDatos(n);
    if (!canEdit) return;
    setEstado("guardando");
    clearTimeout(timer.current);
    timer.current = setTimeout(async () => {
      try {
        await api.patch(`/events/${id}/carteles`, { plantilla: tplId, datos: { ...n, formato: patch.formato || formato } });
        setEstado("guardado");
      } catch (e) { setEstado(""); toast.error(errMsg(e)); }
    }, 700);
  };

  const nuevo = async () => {
    try {
      const r = await api.post("/carteles-personalizados", CARTEL_NUEVO);
      setPropios((ps) => [r.data, ...ps]);
      setTplId(`p:${r.data.id}`);
    } catch (e) { toast.error(errMsg(e)); }
  };

  const lista = (
    <div>
      <div className="text-xs font-semibold text-slate-600 mb-1.5">Cartel</div>
      <div className="space-y-1.5">
        {Object.entries(GRUPOS).flatMap(([g, gl]) => [<div key={g} className="text-[0.625rem] font-mono tracking-widest uppercase text-slate-500 pt-2">{gl}</div>, ...PLANTILLAS.filter((t) => t.grupo === g).map((t) => (
          <button key={t.id} onClick={() => setTplId(t.id)} className={`w-full text-left rounded-lg border px-3 py-2 ${t.id === tplId ? "border-sky-400 bg-sky-50" : "border-slate-200 hover:bg-slate-50"}`} data-testid={`cartel-tpl-${t.id}`}>
            <div className="text-sm font-medium">{t.nombre}</div>
            <div className="text-[0.6875rem] text-slate-500">{t.descripcion}</div>
          </button>
        ))])}
        <div className="text-[0.625rem] font-mono tracking-widest uppercase text-slate-500 pt-2">Mis carteles personalizados</div>
        {propios.map((p) => (
          <button key={p.id} onClick={() => setTplId(`p:${p.id}`)} className={`w-full text-left rounded-lg border px-3 py-2 ${`p:${p.id}` === tplId ? "border-sky-400 bg-sky-50" : "border-slate-200 hover:bg-slate-50"}`} data-testid={`cartel-propio-${p.id}`}>
            <div className="text-sm font-medium truncate">{p.nombre || "Cartel sin nombre"}</div>
            <div className="text-[0.6875rem] text-slate-500">{p.formato} · {p.orientacion} · {p.items.length} elementos</div>
          </button>
        ))}
        {canEdit && (
          <button onClick={nuevo} className="w-full rounded-lg border border-dashed border-slate-300 hover:border-sky-400 hover:bg-sky-50 px-3 py-2 text-sm font-medium text-sky-700 flex items-center gap-2" data-testid="cartel-nuevo">
            <Plus className="w-4 h-4" /> Nuevo cartel personalizado
          </button>
        )}
      </div>
    </div>
  );

  if (!ev) return <div className="p-10 text-slate-500">Cargando…</div>;
  const papel = propio ? propio.formato : formato;
  const f = FORMATOS[formato];
  const escala = ancho ? Math.min(1, ancho / (f.w * PX_MM)) : 0.5; // vista previa: el cartel cabe en pantalla
  return (
    <div className="cartel-raiz min-h-screen bg-slate-100">
      {/* tamaño de papel para la impresión */}
      <style>{`@media print { @page { size: ${papel} ${propio?.orientacion === "vertical" ? "portrait" : "landscape"}; margin: 0; } body { background: #fff !important; } .cartel-raiz { min-height: 0 !important; background: #fff !important; } .cartel-rejilla { display: block !important; padding: 0 !important; gap: 0 !important; } .cartel-vista { transform: none !important; margin: 0 !important; box-shadow: none !important; } .cartel-marco { padding: 0 !important; display: block !important; overflow: visible !important; } .cartel { break-after: avoid; page-break-after: avoid; overflow: hidden; } .cartel-el { outline: none !important; } .cartel-asa { display: none !important; } }`}</style>
      <div className="no-print bg-white border-b border-slate-200 px-4 sm:px-6 py-3 flex flex-wrap items-center gap-3 justify-between sticky top-0 z-10">
        <div className="flex items-center gap-3 min-w-0">
          <Link to={`/events/${id}`} className="text-slate-500 hover:text-slate-900" title="Volver al evento"><ArrowLeft className="w-5 h-5" /></Link>
          <div className="min-w-0">
            <div className="font-display font-bold text-lg leading-tight">Cartelería</div>
            <div className="text-xs text-slate-500 truncate">{ev.name} · {ev.date}</div>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {estado === "guardando" && <span className="text-xs text-sky-700 flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" /> Guardando…</span>}
          {estado === "guardado" && <span className="text-xs text-emerald-700 flex items-center gap-1"><Check className="w-3 h-3" /> Guardado</span>}
          <button onClick={() => window.print()} className="flex items-center gap-2 bg-sky-600 hover:bg-sky-700 text-white font-semibold px-4 py-2 rounded-lg text-sm" data-testid="cartel-print-button">
            <Printer className="w-4 h-4" /> Imprimir {papel} / Guardar PDF
          </button>
        </div>
      </div>

      {propio ? (
        <CartelEditor key={propio.id} inicial={propio} canEdit={canEdit} lista={lista} setEstado={setEstado}
          onCambio={(n) => setPropios((ps) => ps.map((p) => (p.id === n.id ? n : p)))}
          onBorrado={(cid) => { setPropios((ps) => ps.filter((p) => p.id !== cid)); setTplId(PLANTILLAS[0].id); }} />
      ) : (
      <div className="cartel-rejilla grid lg:grid-cols-[22rem_minmax(0,1fr)] gap-4 p-4 sm:p-6">
        <aside className="no-print bg-white border border-slate-200 rounded-xl p-4 space-y-4 h-fit" data-testid="cartel-form">
          {lista}
          {tpl.campos.map((c) => (
            <label key={c.k} className="block">
              <span className="text-xs font-semibold text-slate-600">{c.label}</span>
              {c.tipo === "select" ? (
                <div className="mt-1 grid grid-cols-3 gap-1.5" data-testid={`cartel-campo-${c.k}`}>
                  {c.opciones.map((o) => (
                    <button key={o.v} type="button" disabled={!canEdit} onClick={() => cambiar({ [c.k]: o.v })}
                      className={`h-10 rounded-lg border text-xs font-semibold ${datos[c.k] === o.v ? "border-sky-500 bg-sky-600 text-white" : "border-slate-300 text-slate-700 hover:bg-slate-50"}`}>{o.label}</button>
                  ))}
                </div>
              ) : (
                <input type={c.tipo} value={datos[c.k] || ""} placeholder={c.placeholder} readOnly={!canEdit}
                  onChange={(e) => cambiar({ [c.k]: e.target.value })} data-testid={`cartel-campo-${c.k}`}
                  className="mt-1 w-full h-10 rounded-lg border border-slate-300 px-3 text-sm focus:outline-none focus:border-sky-500" />
              )}
            </label>
          ))}
          {tpl.grupo === "vallas" && (
            <label className="flex items-center justify-between gap-2 rounded-lg border border-slate-300 px-3 py-2 text-sm cursor-pointer">
              <span>Con la señal R-307</span>
              <input type="checkbox" checked={datos.senal !== false} disabled={!canEdit} onChange={(e) => cambiar({ senal: e.target.checked })} className="w-4 h-4 accent-sky-600" data-testid="cartel-senal" />
            </label>
          )}
          <div>
            <div className="text-xs font-semibold text-slate-600 mb-1.5">Tamaño de papel (horizontal)</div>
            <div className="grid grid-cols-2 gap-2">
              {Object.keys(FORMATOS).map((k) => (
                <button key={k} onClick={() => { setFormato(k); cambiar({ formato: k }); }} data-testid={`cartel-formato-${k}`}
                  className={`h-10 rounded-lg border text-sm font-semibold ${formato === k ? "border-sky-500 bg-sky-600 text-white" : "border-slate-300 text-slate-700 hover:bg-slate-50"}`}>{k}</button>
              ))}
            </div>
            <p className="text-[0.6875rem] text-slate-500 mt-2">En el diálogo de impresión, elija el papel {formato}, orientación horizontal y márgenes «Ninguno».</p>
          </div>
        </aside>

        <section ref={marco} className="cartel-marco flex justify-center items-start overflow-hidden min-w-0" data-testid="cartel-preview">
          <div className="cartel-vista shadow-xl origin-top" style={{ transform: `scale(${escala})`, marginBottom: `${(escala - 1) * f.h * PX_MM}px` }}>
            <Cartel tpl={tpl} datos={datos} formato={formato} ine={ev.concello} />
          </div>
        </section>
      </div>
      )}
    </div>
  );
}
